import moment from 'moment-timezone';

export function formatPhoneNumber(phoneNumber) {
  const cleaned = phoneNumber?.replace(/\D/g, '');

  let formatted = cleaned;
  if (cleaned?.length > 3 && cleaned?.length <= 6) {
    formatted = `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  } else if (cleaned?.length > 6 && cleaned?.length <= 10) {
    formatted = `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned?.length > 10) {
    formatted = `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}${cleaned.slice(10)}`;
  }

  return formatted;
}

export function isMobile() {
  return window.innerWidth <= 800;
}

export function getUTCDateParts(dateString, timezone = 'America/New_York') {
  if (!dateString) return null;
  const m = moment(dateString);
  if (!m.isValid()) return null;
  const tzMoment = timezone ? m.tz(timezone) : m;
  return {
    year: tzMoment.year(),
    monthIndex: tzMoment.month(),
    day: tzMoment.date(),
  };
}

export function formatOrderStatus(status, isPast = false) {
  if (!status) return 'Confirmed';
  const s = String(status).trim();
  const lower = s.toLowerCase();
  if (lower === 'delivered') return isPast ? 'Completed' : 'Picked Up';
  if (lower === 'cancel' || lower === 'cancelled') return 'Cancelled';
  if (lower === 'ready for pickup' || lower === 'ready to pickup') return 'Ready to Pickup';
  if (lower === 'pending' || lower === 'confirmed' || lower === 'placed') return 'Confirmed';
  return s;
}

export function isPickupWindowStarted(startDateStr, endDateStr) {
  if (!startDateStr) return false;
  const now = new Date();
  const start = new Date(startDateStr);
  const end = endDateStr ? new Date(endDateStr) : null;

  if (isNaN(start.getTime())) return false;
  return now >= start && (!end || isNaN(end.getTime()) || now <= end);
}

export function formatPickupWindow(startDateStr, endDateStr, timezone = 'America/New_York') {
  if (!startDateStr) return null;

  const start = moment(startDateStr);
  if (!start.isValid()) return null;

  const startParts = getUTCDateParts(startDateStr, timezone);
  const endParts = endDateStr ? getUTCDateParts(endDateStr, timezone) : null;
  if (!startParts) return null;

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const startMonth = monthNames[startParts.monthIndex];
  const startDay = startParts.day;
  const startYear = startParts.year;

  if (!endParts) {
    return `${startMonth} ${startDay}, ${startYear}`;
  }

  const endMonth = monthNames[endParts.monthIndex];
  const endDay = endParts.day;
  const endYear = endParts.year;

  if (startMonth === endMonth && startYear === endYear) {
    if (startDay === endDay) {
      return `${startMonth} ${startDay}, ${startYear}`;
    }
    return `${startMonth} ${startDay}–${endDay}, ${startYear}`;
  } else if (startYear === endYear) {
    return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${startYear}`;
  } else {
    return `${startMonth} ${startDay}, ${startYear} – ${endMonth} ${endDay}, ${endYear}`;
  }
}

export function matchCartItemsToBundles(cartItems = [], bundleItems = []) {
  let appliedBundles = [];
  let remainingItems = [];
  let newCartItems = [];

  // Create a map of SKU ID -> available quantity
  const availableQuantities = {};
  cartItems.forEach((item) => {
    availableQuantities[item.sku_id] = Number(item.quantity || 0);
  });

  // Track allocations: item_id -> array of { bundle_id, bundle_name, bundleQuantity, bundleProductQuantity, ratio_price }
  const itemBundleAllocations = {};

  // Sort bundleItems by size in descending order to match larger/more-specific bundles first
  const sortedBundleItems = [...bundleItems].sort((a, b) => {
    const aLen = (a?.bundleProduct || []).length;
    const bLen = (b?.bundleProduct || []).length;
    return bLen - aLen;
  });

  for (const bundleItem of sortedBundleItems) {
    const bundleProductData = bundleItem?.bundleProduct || [];
    if (bundleProductData.length === 0) continue;

    // Check how many times we can apply this bundle
    let maxBundleQty = Infinity;
    for (const bp of bundleProductData) {
      const skuId = bp?.sku?.sku_id || bp?.sku_id;
      const reqQty = Number(bp.bundle_quantity || 1);
      const avail = availableQuantities[skuId] || 0;
      const possibleQty = Math.floor(avail / reqQty);
      if (possibleQty < maxBundleQty) {
        maxBundleQty = possibleQty;
      }
    }

    if (maxBundleQty > 0 && maxBundleQty !== Infinity) {
      // Consume the quantities
      for (const bp of bundleProductData) {
        const skuId = bp?.sku?.sku_id || bp?.sku_id;
        const reqQty = Number(bp.bundle_quantity || 1);
        availableQuantities[skuId] -= reqQty * maxBundleQty;
      }

      // Add to appliedBundles
      const productArr = [];
      for (const bp of bundleProductData) {
        const skuId = bp?.sku?.sku_id || bp?.sku_id;
        const cartItemDetails = cartItems.find((n) => n.sku_id == skuId);
        const reqQty = Number(bp.bundle_quantity || 1);
        productArr.push({
          ...cartItemDetails,
          ratio_price: bp?.ratio_price,
        });

        // Track allocation
        if (cartItemDetails) {
          if (!itemBundleAllocations[cartItemDetails.item_id]) {
            itemBundleAllocations[cartItemDetails.item_id] = [];
          }
          itemBundleAllocations[cartItemDetails.item_id].push({
            bundle_id: bundleItem.bundle_id,
            bundle_name: bundleItem.bundle_name,
            bundleQuantity: maxBundleQty,
            bundleProductQuantity: reqQty,
            ratio_price: bp?.ratio_price,
          });
        }
      }

      appliedBundles.push({
        ...bundleItem,
        quantity: maxBundleQty,
        productArr: productArr,
      });
    }
  }

  // Populate remainingItems
  cartItems.forEach((item) => {
    const leftover = availableQuantities[item.sku_id] || 0;
    if (leftover > 0) {
      remainingItems.push({
        ...item,
        quantity: leftover,
      });
    }
  });

  // Construct newCartItems containing updated discount and bundle messages
  cartItems.forEach((cartItemDetails) => {
    const allocations = itemBundleAllocations[cartItemDetails.item_id] || [];
    const discount_price =
      cartItemDetails?.product_details?.skus?.[0]?.discount_price == null ||
      cartItemDetails?.product_details?.skus?.[0]?.discount_price == undefined ||
      cartItemDetails?.product_details?.skus?.[0]?.discount_price == 0
        ? parseFloat(
            (Number(cartItemDetails?.product_details?.skus?.[0]?.regular_price) || 0).toFixed(2)
          )
        : cartItemDetails?.product_details?.skus?.[0]?.discount_price;

    if (allocations.length > 0) {
      const totalAllocatedQty = allocations.reduce(
        (sum, alloc) => sum + alloc.bundleProductQuantity * alloc.bundleQuantity,
        0
      );
      const finalQuantity = cartItemDetails.quantity - totalAllocatedQty;

      // Base discount for the remaining standalone quantity (selling price)
      let totalDiscount = parseFloat(finalQuantity * discount_price);

      // Add the selling price from each bundle allocation
      allocations.forEach((alloc) => {
        const sku = cartItemDetails?.product_details?.skus?.[0];
        const originalDiscountPrice = parseFloat(sku?.original_discount_price);
        const sku_bundle_price =
          !isNaN(originalDiscountPrice) &&
          originalDiscountPrice >= 0 &&
          originalDiscountPrice < parseFloat(sku?.regular_price || 0)
            ? originalDiscountPrice
            : sku?.discount_price !== null &&
                sku?.discount_price !== undefined &&
                sku?.discount_price !== '' &&
                parseFloat(sku?.discount_price) < parseFloat(sku?.regular_price || 0)
              ? parseFloat(sku?.discount_price)
              : parseFloat(sku?.regular_price || 0);

        const basePriceOfBundleQty = sku_bundle_price * alloc.bundleProductQuantity;
        const discountedPriceOfBundleQty = Math.max(
          0,
          basePriceOfBundleQty - parseFloat(alloc.ratio_price || 0)
        );
        totalDiscount += parseFloat(alloc.bundleQuantity * discountedPriceOfBundleQty);
      });

      // Prepare bundle message
      const bundleMessages = allocations.map(
        (alloc) =>
          `${alloc.bundleQuantity} ${alloc.bundle_name} bundle is applied to that product with a quantity of ${alloc.bundleQuantity * alloc.bundleProductQuantity}.`
      );

      newCartItems.push({
        ...cartItemDetails,
        totalDiscount: parseFloat(totalDiscount.toFixed(2)),
        bundleMessage: bundleMessages.join(' '),
        bundle_id: allocations[0].bundle_id,
        bundle_quantity: allocations[0].bundleQuantity,
      });
    } else {
      // Standalone item: just quantity * discount_price
      const totalDiscount = parseFloat(cartItemDetails.quantity * discount_price);
      newCartItems.push({
        ...cartItemDetails,
        totalDiscount: parseFloat(totalDiscount.toFixed(2)),
      });
    }
  });

  const finalCartItems = cartItems.map((n) => {
    const findCartItem = newCartItems.find((k) => k.item_id == n.item_id);
    if (findCartItem) {
      return {
        ...findCartItem,
      };
    } else {
      return {
        ...n,
      };
    }
  });

  // Calculate totalDiscountAmount for productArr of appliedBundles (sets component selling price in bundle)
  appliedBundles = appliedBundles.map((n) => {
    return {
      ...n,
      productArr: n?.productArr.map((k) => {
        const bundleQuantityData =
          n.bundleProduct.find((m) => (m?.sku?.sku_id || m?.sku_id) === k.sku_id)
            ?.bundle_quantity || 1;
        const productBundleQuantity = bundleQuantityData * n.quantity;
        const sku = k?.product_details?.skus?.[0];
        const originalDiscountPrice = parseFloat(sku?.original_discount_price);
        const sku_bundle_price =
          !isNaN(originalDiscountPrice) &&
          originalDiscountPrice >= 0 &&
          originalDiscountPrice < parseFloat(sku?.regular_price || 0)
            ? originalDiscountPrice
            : sku?.discount_price !== null &&
                sku?.discount_price !== undefined &&
                sku?.discount_price !== '' &&
                parseFloat(sku?.discount_price) < parseFloat(sku?.regular_price || 0)
              ? parseFloat(sku?.discount_price)
              : parseFloat(sku?.regular_price || 0);

        const basePriceOfBundleQty = sku_bundle_price * bundleQuantityData;
        const discountedPriceOfBundleQty = Math.max(
          0,
          basePriceOfBundleQty - parseFloat(k.ratio_price || 0)
        );
        return {
          ...k,
          quantity: productBundleQuantity,
          totalDiscountAmount: parseFloat((n.quantity * discountedPriceOfBundleQty).toFixed(2)),
        };
      }),
    };
  });

  return {
    appliedBundles,
    remainingItems,
    finalCartItems,
  };
}

// export function matchCartItemsToBundles(cartItems = [], bundleItems = []) {
//     let appliedBundles = [];
//     let remainingItems = [];
//     let cartSkuId = cartItems.map(item => item?.sku_id);

//     for (const bundleItem of bundleItems) {
//         const bundleProductData = bundleItem?.bundleProduct;
//         const bundleSkuIds = bundleProductData.map(product => product?.sku?.sku_id);

//         if (bundleSkuIds.every(skuId => cartSkuId.includes(skuId))) {
//             appliedBundles.push({ ...bundleItem });
//             cartSkuId = cartSkuId.filter(skuId => !bundleSkuIds.includes(skuId));
//         }
//     }

//     cartSkuId.forEach(skuId => {
//         const item = cartItems.find(cartItem => cartItem.sku_id === skuId);
//         if (item) remainingItems.push({ ...item });
//     });

//     appliedBundles = appliedBundles.map(bundle => {
//         const updatedBundleProducts = [];
//         let validBundle = true;

//         for (const product of bundle.bundleProduct) {
//             const skuId = product?.sku?.sku_id;
//             const bundleQuantity = product?.bundle_quantity;
//             const cartItem = cartItems.find(item => item.sku_id === skuId);

//             if (!cartItem || cartItem.quantity < bundleQuantity) {
//                 validBundle = false;
//                 break;
//             }

//             const remainingQuantity = cartItem.quantity - bundleQuantity;
//             updatedBundleProducts.push({
//                 ...product,
//                 cartItemDetails: { ...cartItem },
//                 appliedBundleProductQuantity: bundleQuantity,
//                 remainingQuantity,
//             });
//         }

//         if (validBundle) {
//             const bundleQuantities = updatedBundleProducts.map(product =>
//                 Math.floor(product.cartItemDetails.quantity / product.appliedBundleProductQuantity)
//             );
//             const bundleQuantity = Math.min(...bundleQuantities);

//             const updatedBundle = { ...bundle, quantity: bundleQuantity };

//             updatedBundleProducts.forEach(product => {
//                 const remainder = product.cartItemDetails.quantity % product.appliedBundleProductQuantity;
//                 if (remainder > 0) {
//                     remainingItems.push({
//                         ...product.cartItemDetails,
//                         quantity: remainder,
//                     });
//                 }
//             });

//             return updatedBundle;
//         } else {
//             remainingItems.push(
//                 ...bundle.bundleProduct.map(product => {
//                     const cartItem = cartItems.find(item => item.sku_id === product?.sku?.sku_id);
//                     return cartItem ? { ...cartItem } : null;
//                 }).filter(Boolean)
//             );
//             return null;
//         }
//     }).filter(Boolean);

//     return {
//         appliedBundles,
//         remainingItems,
//     };
// }

export function formatAmount(val) {
  if (val === null || val === undefined || val === '') return '0.00';
  const num = Number(val);
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatCurrency(val, symbol = '$') {
  return `${symbol}${formatAmount(val)}`;
}
